import {Button, Drawer, HStack, Icon, Input, Portal, Text, VStack, Field, Flex, Separator} from "@chakra-ui/react"
import {IoCloseSharp} from "react-icons/io5";
import {HiOutlinePlus, HiOutlineTrash} from "react-icons/hi2";
import ControlledInput from "@/components/ui/foundation/ControlledInput";
import {useEffect, useState} from "react";
import uuid4 from "uuid4";
import ConfirmationButton from "@/components/ui/foundation/ConfirmationButton";
import useEditableList from "@/components/hooks/useEditableList";
import {mapTrainingPhrasesToListItems, mapApiEntitiesToFormEntities, normalizeMentionResponse} from "@/services/utilities/Mapper";
import EntitiesAccordion from "@/pages/scenario/components/EntitiesAccordion";
import MentionTextArea from "@/pages/scenario/components/MentionTextArea";
import EndpointParamsModal from "@/pages/scenario/components/EndpointParamsModal";
import WorkerMappingModal from "@/pages/scenario/components/WorkerMappingModal";
import useEndpointVariables from "@/components/hooks/useEndpointVariables";
import useWorkerVariables from "@/components/hooks/useWorkerVariables";
import {createEmptyScoringConfig} from "@/scoringUi";

const createEmptyEntity = () => {
    return {
        id: uuid4(),
        name: "",
        displayName: "",
        completeResponse: "",
        response: "",
        nextIntent: "",
        examples: [{id: uuid4(), value: "", disabled: false}],
        skipPhrases: [{id: uuid4(), value: "", disabled: false}],
        transitions: [{id: uuid4(), destination: "", condition: ""}],
        isRequired: true,
        isConditional: false,
        modifyResponses: false,
        isFinishing: false,
        endConversation: false,
        // Every criterion gets a stable key at creation time -- Entity.Id is
        // regenerated by the backend on every save, so it can't identify a
        // criterion across saves for recruitment-side override matching.
        scoring: {...createEmptyScoringConfig(), criterionKey: uuid4()},
    }
}

const IntentDrawerContent = ({selectedIntent, scenario, onSave, onDelete, isLoading, setOpen}) => {
    const [intentName, setIntentName] = useState(selectedIntent?.name ?? "");
    const [finalResponse, setFinalResponse] = useState(
        normalizeMentionResponse(selectedIntent?.finalResponse ?? "")
    );
    const {
        items: trainingPhrases,
        setItems: setTrainingPhrases,
        addItem: handleAddTrainingPhrase,
        removeItem: handleRemovePhrase,
        editItem: handleEditFieldClick,
        changeItem: handleFieldChange,
    } = useEditableList(
        selectedIntent
            ? [...mapTrainingPhrasesToListItems(selectedIntent.trainingPhrases), {id: uuid4(), value: "", disabled: false}]
            : [{id: uuid4(), value: "", disabled: false}]
    );
    const [entities, setEntities] = useState(
        selectedIntent ? mapApiEntitiesToFormEntities(selectedIntent.entities) : [createEmptyEntity()]
    );

    const parsedWorkerValues = (() => {
        const raw = selectedIntent?.dynamicWorkerValues;
        if (typeof raw === 'string' && raw) {
            try { return JSON.parse(raw); } catch { return []; }
        }
        return Array.isArray(raw) ? raw : [];
    })();

    const {
        endpointVariables,
        dynamicValues,
        paramModalOpen,
        selectedEndpoint,
        handleMentionClick,
        handleMentionDelete,
        handleVariableSelect,
        handleParamValuesSave,
        closeParamModal,
        getInitialParamValues,
        getInitialBodySchemaValues,
    } = useEndpointVariables(selectedIntent?.dynamicValues ?? []);

    const {
        workerVariables,
        dynamicWorkerValues,
        workerModalOpen,
        selectedWorker,
        handleWorkerMentionClick,
        handleWorkerMentionDelete,
        handleWorkerVariableSelect,
        handleWorkerMappingSave,
        closeWorkerModal,
        getInitialWorkerMappings,
    } = useWorkerVariables(parsedWorkerValues);

    const mentionSets = [
        {
            wakeUpChar: '#',
            variables: endpointVariables,
            wrapMention: true,
            color: 'blue.300',
            bg: 'rgba(66,153,225,0.15)',
            onMentionClick: handleMentionClick,
            onVariableSelect: handleVariableSelect,
            onMentionDelete: handleMentionDelete,
        },
        {
            wakeUpChar: '$',
            variables: workerVariables,
            wrapMention: true,
            color: 'green.300',
            bg: 'rgba(72,187,120,0.15)',
            onMentionClick: handleWorkerMentionClick,
            onVariableSelect: handleWorkerVariableSelect,
            onMentionDelete: handleWorkerMentionDelete,
        },
    ];

    const handleEntityChange = (entity) => {
        setEntities(prev => prev.map(item => item.id === entity.id ? entity : item));
    }

    const handleRemoveEntity = (entityId) => {
        setEntities(prev => {
            if (prev.length <= 1) return prev;
            return prev.filter(item => item.id !== entityId);
        });
    }

    const handleAddEntity = () => {
        setEntities(prev => [...prev, createEmptyEntity()]);
    }

    const handleDuplicateEntity = (entity) => {
        const duplicate = {
            ...entity,
            id: uuid4(),
            examples: entity.examples.map(e => ({ ...e, id: uuid4() })),
            skipPhrases: entity.skipPhrases.map(s => ({ ...s, id: uuid4() })),
            transitions: entity.transitions.map(t => ({ ...t, id: uuid4() })),
        };
        setEntities(prev => {
            const index = prev.findIndex(e => e.id === entity.id);
            const next = [...prev];
            next.splice(index + 1, 0, duplicate);
            return next;
        });
    };

    const handleReorderEntities = (reordered) => setEntities(reordered);

    const handleOnSave = () => {
        onSave && onSave({
            ...selectedIntent,
            name: intentName,
            finalResponse,
            trainingPhrases,
            entities,
            dynamicValues,
            dynamicWorkerValues,
        });
    }

    const handleDelete = () => {
        onDelete && onDelete(selectedIntent);
    }

    return (
        <>
            <Drawer.Header>
                <HStack justifyContent={'space-between'} w={'full'}>
                    <VStack alignItems={'flex-start'} gap={0}>
                        <Text color={'white'}>Intent Builder</Text>
                        <Text color={'text.grey'}
                              w={'100%'}
                              whiteSpace="nowrap"
                              noOfLines={1}
                              overflow="hidden"
                              textOverflow="ellipsis">
                            Scenario title: {scenario?.name}
                        </Text>
                    </VStack>
                    <Icon fontSize={32} color={'text.grey'} onClick={() => setOpen(false)}
                          cursor={'pointer'}>
                        <IoCloseSharp/>
                    </Icon>
                </HStack>
            </Drawer.Header>
            <Drawer.Body>
                <Flex w={'full'} alignItems={'center'} justifyContent={'center'}>
                    <VStack w={{sm: 'full', xl: '50%'}} alignItems={'flex-start'}>
                        <Field.Root required>
                            <Field.Label color={'white'} fontSize={18}>
                                Intent name <Field.RequiredIndicator/>
                            </Field.Label>
                            <Input variant={'primary'} placeholder="Enter intent name" mb={2} value={intentName} onChange={(e) => setIntentName(e.target.value)} />
                        </Field.Root>

                        <Field.Root>
                            <Field.Label color={'white'} fontSize={18}>
                                Intent initation <Field.RequiredIndicator/>
                            </Field.Label>
                            <Field.HelperText>Enter words or phrases that trigger this intention.</Field.HelperText>
                        </Field.Root>
                        {trainingPhrases?.map((phrase) => (
                            <ControlledInput key={phrase.id}
                                             fieldId={phrase.id}
                                             isDisabled={phrase.disabled}
                                             canRemove={trainingPhrases.length > 1}
                                             value={phrase.value}
                                             placeholder={"Example of intent initiation"}
                                             onTrashClick={() => handleRemovePhrase(phrase.id)}
                                             onChangeText={(value) => handleFieldChange(phrase.id, value)}
                                             onEditClick={handleEditFieldClick}/>
                        ))}

                        <Button variant="outlined" textDecoration='unset' onClick={handleAddTrainingPhrase}
                                alignSelf={'end'} mt={2}>
                            <Icon fontSize={46}>
                                <HiOutlinePlus/>
                            </Icon>
                            <Text>Add example of trigger phrase</Text>
                        </Button>

                        <Separator variant="solid" pb={3} w={'full'} borderColor={'gray.700'} mt={4} />

                        <Field.Root required>
                            <Field.Label color={'white'} fontSize={18}>
                                Starting message <Field.RequiredIndicator/>
                            </Field.Label>
                            <Field.HelperText>The first message sent after this intent is triggered.</Field.HelperText>
                            <MentionTextArea
                                contentContainerProps={{w: 'full'}}
                                onChange={setFinalResponse}
                                value={finalResponse}
                                mentionSets={mentionSets}
                                variant={'dark'}
                                placeholder={'Enter intent starting message'}
                                mb={6}
                            />
                        </Field.Root>

                        <Separator variant="solid" pb={3} w={'full'} borderColor={'gray.700'} />

                        <EntitiesAccordion
                            entities={entities}
                            onEntityChange={handleEntityChange}
                            onRemoveEntity={handleRemoveEntity}
                            onAddEntity={handleAddEntity}
                            onReorder={handleReorderEntities}
                            onDuplicateEntity={handleDuplicateEntity}
                            intents={scenario?.intents}
                        />

                    </VStack>
                </Flex>
            </Drawer.Body>
            <Drawer.Footer borderRadius={16}>
                <HStack justifyContent={selectedIntent?.id ? 'space-between' : 'flex-end'} w={'full'}>
                    {selectedIntent?.id &&
                        <ConfirmationButton
                            onConfirm={handleDelete}
                            variant="outlined"
                            label={"Delete intent"}
                            isLoading={isLoading}
                            icon={<HiOutlineTrash/>}
                        />
                    }
                    <ConfirmationButton
                        onConfirm={handleOnSave}
                        withoutConfirmation
                        variant="primary"
                        isLoading={isLoading}
                        label={selectedIntent?.id ? "Update intent" : "Save intent"}
                    />
                </HStack>
            </Drawer.Footer>

            <EndpointParamsModal
                isOpen={paramModalOpen}
                onClose={closeParamModal}
                onSave={handleParamValuesSave}
                endpoint={selectedEndpoint}
                initialValues={getInitialParamValues()}
                initialBodyValues={getInitialBodySchemaValues()}
            />

            <WorkerMappingModal
                isOpen={workerModalOpen}
                onClose={closeWorkerModal}
                onSave={handleWorkerMappingSave}
                worker={selectedWorker}
                initialMappings={getInitialWorkerMappings()}
            />
        </>
    );
}

const CreateIntentDrawer = ({isOpen, setOpen, selectedIntent, scenario, onSave, onDelete, isLoading}) => {
    return (
        <Drawer.Root open={isOpen} onOpenChange={(e) => setOpen(e.open)} size={'xl'} placement={'end'}>
            <Portal>
                <Drawer.Backdrop/>
                <Drawer.Positioner padding="4">
                    <Drawer.Content minW={'100%'} bg={"bg.drawer"} borderRadius={16}>
                        <IntentDrawerContent
                            key={selectedIntent?.id ?? 'new'}
                            selectedIntent={selectedIntent}
                            scenario={scenario}
                            onSave={onSave}
                            onDelete={onDelete}
                            isLoading={isLoading}
                            setOpen={setOpen}
                        />
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    )
}

export default CreateIntentDrawer;
